import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../services/api';

const OnboardingSlidesPage: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const slides = [
    {
      icon: '👥',
      title: 'Explore Communities',
      description: 'Join communities to connect with players & coaches who share your passion.',
      bgColor: 'bg-blue-100',
      iconColor: 'text-blue-600'
    },
    {
      icon: '🏆',
      title: 'Play Tournaments',
      description: 'Compete in official & community tournaments to showcase your skills and win prizes.',
      bgColor: 'bg-yellow-100',
      iconColor: 'text-yellow-600'
    },
    {
      icon: '🏅',
      title: 'Showcase Achievements',
      description: 'Upload achievements & get verified recognition from the sports community.',
      bgColor: 'bg-green-100',
      iconColor: 'text-green-600'
    },
    {
      icon: '⭐',
      title: 'Get Started',
      description: 'Ready to begin your sports journey? Let\'s get you connected with the community!',
      bgColor: 'bg-purple-100',
      iconColor: 'text-purple-600'
    }
  ];

  useEffect(() => {
    // Check if user came from sports selection
    const signupData = localStorage.getItem('signupData');
    if (!signupData) {
      navigate('/signup');
    }
  }, [navigate]);

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      handleFinish();
    }
  };

  const handleSkip = () => {
    handleFinish();
  };

  const handleFinish = async () => {
    setLoading(true);

    try {
      const signupData = JSON.parse(localStorage.getItem('signupData') || '{}');
      
      // Register the user
      const response = await api.post('/auth/signup', signupData);
      
      if (response.data.success) {
        // Store token
        localStorage.setItem('token', response.data.data.token);
        
        // Clear signup data
        localStorage.removeItem('signupData');
        
        toast.success(response.data.message);
        
        // Redirect based on role
        if (signupData.role === 'admin') {
          navigate('/admin/dashboard');
        } else {
          navigate('/home');
        }
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const currentSlideData = slides[currentSlide];

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/onboarding/sports" className="text-gray-600 hover:text-gray-800 mb-4 inline-block">
            ← Back
          </Link>
          <div className="flex justify-center space-x-2 mb-6">
            {slides.map((_, index) => (
              <div 
                key={index}
                className={`w-2 h-2 rounded-full ${
                  index === currentSlide ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
          <button 
            onClick={handleSkip}
            className="text-gray-600 hover:text-gray-800"
          >
            Skip
          </button>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className={`w-24 h-24 ${currentSlideData.bgColor} rounded-full flex items-center justify-center mx-auto mb-6`}>
            <span className={`text-3xl ${currentSlideData.iconColor}`}>
              {currentSlideData.icon}
            </span>
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {currentSlideData.title}
          </h2>
          
          <p className="text-gray-600 mb-8">
            {currentSlideData.description}
          </p>
          
          <button 
            onClick={handleNext}
            disabled={loading}
            className="w-full bg-gray-900 text-white py-3 px-4 rounded-lg font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating Account...' : 
             currentSlide === slides.length - 1 ? 'Start Your Journey →' : 'Continue →'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingSlidesPage;